import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Bookmark, BookmarkCheck, Check, ChevronLeft, ChevronRight, LayoutGrid, Share2, X } from 'lucide-react';
import { cardsAPI, studyAPI } from '../../services/api';
import RichContent, { isHtml } from '../RichText/RichContent';
import UPSCQuestion from '../UPSCQuestion/UPSCQuestion';
import ImportantTermText from '../ImportantTermText/ImportantTermText';
import './CardBrowser.css';
import './MainsPracticeSession.css';

const toText = value => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.map(toText).filter(Boolean).join('\n');
  if (typeof value === 'object') return toText(value.text ?? value.value ?? value.label ?? value.html ?? value.content ?? '');
  return '';
};

function formatDate(date) {
  const parsed = new Date(`${date}T12:00:00+05:30`);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function FormattedAnswer({ text }) {
  if (isHtml(text)) return <RichContent html={text} />;
  let inInterestedStudentsSection = false;
  return text.split('\n').map((line, index) => {
    if (/^\s*For Interested Students\b/i.test(line)) inInterestedStudentsSection = true;
    if (line.trim()) {
      return <p key={index}><ImportantTermText text={line} disableLinks={inInterestedStudentsSection} /></p>;
    }
    return <div key={index} className="mp-answer-gap" />;
  });
}

export default function MainsPracticeSession() {
  const { date = '' } = useParams();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [bookmarkBusy, setBookmarkBusy] = useState(false);
  const [bookmarkError, setBookmarkError] = useState('');
  const [shareMessage, setShareMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [navOpen, setNavOpen] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    setItems([]);
    setSelectedId(null);
    studyAPI.getQuestionBank({ date, examType: 'Mains' })
      .then(response => {
        if (!active) return;
        const list = (Array.isArray(response.data?.cards) ? response.data.cards : [])
          .filter(item => item?.card?._id);
        setItems(list);
        setSelectedId(list[0]?.card._id || null);
      })
      .catch(loadError => {
        console.error('Could not load Mains practice questions:', loadError);
        if (active) setError(loadError.response?.data?.message || 'Could not load questions for this date.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [date]);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const previous = [html.style.overflow, body.style.overflow];
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    return () => {
      html.style.overflow = previous[0];
      body.style.overflow = previous[1];
    };
  }, []);

  const { flat, groups } = useMemo(() => {
    const bySubject = new Map();
    items.forEach(item => {
      const subject = toText(item.card.subject).trim() || 'General';
      if (!bySubject.has(subject)) bySubject.set(subject, []);
      bySubject.get(subject).push(item);
    });

    const all = [];
    const sections = [];
    bySubject.forEach((subjectItems, subject) => {
      sections.push({ subject, ids: subjectItems.map(item => String(item.card._id)) });
      all.push(...subjectItems);
    });
    return { flat: all, groups: sections };
  }, [items]);

  const idx = flat.findIndex(item => String(item.card._id) === String(selectedId));
  const item = idx >= 0 ? flat[idx] : null;
  const card = item?.card || null;
  const answer = toText(card?.answer) || toText(card?.modelAnswer) || toText(card?.explanation);

  useEffect(() => {
    setRevealed(false);
    setBookmarkError('');
    setShareMessage('');
    setIsBookmarked(Boolean(item?.progress?.isBookmarked));
    contentRef.current?.scrollTo?.(0, 0);
  }, [selectedId, item?.progress?.isBookmarked]);

  const goTo = id => {
    setSelectedId(id);
    setNavOpen(false);
  };
  const goPrev = useCallback(() => {
    if (idx > 0) setSelectedId(flat[idx - 1].card._id);
  }, [idx, flat]);
  const goNext = useCallback(() => {
    if (idx >= 0 && idx < flat.length - 1) setSelectedId(flat[idx + 1].card._id);
  }, [idx, flat]);

  useEffect(() => {
    const onKeyDown = event => {
      if (event.key === 'Escape') {
        setNavOpen(false);
        return;
      }
      const target = event.target;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'SELECT'
        || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key === 'ArrowRight') goNext();
      if (event.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [goNext, goPrev]);

  const toggleBookmark = async () => {
    if (!card || bookmarkBusy) return;
    setBookmarkError('');
    setBookmarkBusy(true);
    try {
      const response = await cardsAPI.bookmark(card._id);
      setIsBookmarked(Boolean(response.data?.isBookmarked));
    } catch (bookmarkFailure) {
      console.error('Could not update Mains question bookmark:', bookmarkFailure);
      setBookmarkError(bookmarkFailure.response?.data?.message || 'Could not update bookmark. Please try again.');
    } finally {
      setBookmarkBusy(false);
    }
  };

  const shareQuestion = async () => {
    if (!card) return;
    const url = `${window.location.origin}/card/${card._id}`;
    setShareMessage('');
    try {
      if (navigator.share) {
        await navigator.share({ title: 'CivilFox Mains question', url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setShareMessage('Link copied');
    } catch (shareError) {
      if (shareError.name === 'AbortError') return;
      console.error('Could not share Mains question:', shareError);
      setShareMessage('Could not share this question.');
    }
  };

  const screen = (
    <div className="cb mp-practice">
      <aside className={`cb-side ${navOpen ? 'open' : ''}`} aria-label="Mains questions">
        <div className="cb-side-body">
          <div className="cb-side-top">
            <h2 className="mp-side-title">Questions</h2>
            <button type="button" className="cb-x" onClick={() => setNavOpen(false)} aria-label="Close questions panel">
              <X size={18} />
            </button>
          </div>
          <nav className="cb-subjects">
            {groups.map(group => (
              <section key={group.subject} className={group.ids.includes(String(selectedId)) ? 'on' : ''}>
                <h2><i className="cb-dot" />{group.subject}</h2>
                <div className="cb-tiles">
                  {group.ids.map((id, number) => (
                    <button
                      key={id}
                      type="button"
                      className={`cb-tile ${id === String(selectedId) ? 'cur' : ''}`}
                      aria-label={`${group.subject}, question ${number + 1}`}
                      aria-current={id === String(selectedId) ? 'true' : undefined}
                      onClick={() => goTo(id)}
                    >
                      {number + 1}
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </nav>
          <button type="button" className="cb-back" onClick={() => navigate('/mains')}>
            <ArrowLeft size={18} />Back to dates
          </button>
        </div>
      </aside>

      <div className="cb-content" ref={contentRef}>
        <div className="cb-bar">
          <button type="button" className="cb-grid-btn" onClick={() => setNavOpen(true)} aria-label="Show question list">
            <LayoutGrid size={18} />
          </button>
          <h1 className="mp-title">Mains practice · {formatDate(date)}</h1>
        </div>

        {loading ? (
          <div className="cb-state"><span className="cb-spin" role="status" aria-label="Loading questions" /></div>
        ) : error ? (
          <p className="cb-state cb-answer-feedback" role="alert">{error}</p>
        ) : !card ? (
          <p className="cb-state cb-muted">No Mains questions found for {formatDate(date)}.</p>
        ) : (
          <>
            <div className="cb-nav">
              <span className="cb-pos">Question {idx + 1} of {flat.length}</span>
            </div>
            <article>
              <section className="cb-card">
                <div className="cb-card-heading">
                  <div className="cb-tags">
                    {toText(card.subject) && <span>{toText(card.subject)}</span>}
                    {toText(card.mainsGSPaper) && <span>{toText(card.mainsGSPaper)}</span>}
                    <span>Mains</span>
                  </div>
                  <div className="cb-card-actions" aria-label="Question actions">
                    <button
                      type="button"
                      className={`cb-icon-action ${isBookmarked ? 'is-bookmarked' : ''}`}
                      onClick={toggleBookmark}
                      disabled={bookmarkBusy}
                      aria-label={isBookmarked ? 'Remove bookmark' : 'Bookmark question'}
                      aria-pressed={isBookmarked}
                      title={isBookmarked ? 'Remove bookmark' : 'Bookmark question'}
                    >
                      {isBookmarked ? <BookmarkCheck size={20} /> : <Bookmark size={20} />}
                    </button>
                    <button type="button" className="cb-icon-action" onClick={shareQuestion} aria-label="Share question" title="Share question">
                      {shareMessage === 'Link copied' ? <Check size={20} /> : <Share2 size={20} />}
                    </button>
                  </div>
                </div>

                <div className="cb-qtext"><UPSCQuestion text={toText(card.question)} /></div>
                {(bookmarkError || (shareMessage && shareMessage !== 'Link copied')) && (
                  <p className="cb-answer-feedback" role="alert">{bookmarkError || shareMessage}</p>
                )}
              </section>

              {revealed ? (
                <section className="mp-answer-section" aria-label="Model answer">
                  <h2>Model answer</h2>
                  {answer
                    ? <div className="mp-answer-content"><FormattedAnswer text={answer} /></div>
                    : <p className="cb-muted">No model answer available for this question.</p>}
                </section>
              ) : (
                <div className="mp-answer-actions">
                  <button type="button" className="mp-show-answer" onClick={() => setRevealed(true)}>
                    Show answer
                  </button>
                </div>
              )}
            </article>
            {flat.length > 1 && (
              <div className="cb-nav mp-question-nav">
                <button type="button" className="cb-nbtn" onClick={goPrev} disabled={idx <= 0} aria-label="Previous question">
                  <ChevronLeft size={18} />
                </button>
                <span className="cb-pos">{idx + 1}/{flat.length}</span>
                <button type="button" className="cb-nbtn" onClick={goNext} disabled={idx >= flat.length - 1} aria-label="Next question">
                  <ChevronRight size={18} />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <button
        type="button"
        className={`cb-backdrop ${navOpen ? 'show' : ''}`}
        onClick={() => setNavOpen(false)}
        aria-label="Close question list"
      />
    </div>
  );

  return createPortal(screen, document.body);
}
